import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import MovieCard from "@/components/base/MovieCard";
import Pagination from "@/components/base/Pagination";
import FilterPanel from "@/pages/category/components/FilterPanel";
import usePageMeta from "@/hooks/usePageMeta";
import {
  allWorks,
  filterWorks,
  getGenreOptions,
  getRegionOptions,
  getYearOptions,
  PAGE_SIZE,
  paginate,
  sortWorks,
  TYPE_LABELS,
} from "@/lib/works";
import type { SortKey, WorkFilters } from "@/types/work";

const SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: "updated", label: "按更新时间" },
  { value: "heat", label: "按热度" },
  { value: "rating", label: "按评分" },
];

const EMPTY_FILTERS: WorkFilters = { types: [], years: [], regions: [], genres: [] };

export default function Category() {
  const { type: typeParam } = useParams<{ type: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const listRef = useRef<HTMLDivElement>(null);
  const [filterOpen, setFilterOpen] = useState(false);

  const validType = typeParam === "movie" || typeParam === "tv" ? typeParam : undefined;
  const sortParam = searchParams.get("sort");
  const sortKey: SortKey = sortParam === "heat" || sortParam === "rating" ? sortParam : "updated";
  const rawPage = Number(searchParams.get("page") ?? "1");
  const page = Number.isFinite(rawPage) && rawPage > 0 ? Math.floor(rawPage) : 1;

  const [filters, setFilters] = useState<WorkFilters>(
    validType ? { ...EMPTY_FILTERS, types: [validType] } : EMPTY_FILTERS
  );

  useEffect(() => {
    setFilters((prev) => ({ ...prev, types: validType ? [validType] : [] }));
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("page");
      return next;
    }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validType]);

  const options = useMemo(
    () => ({
      years: getYearOptions(allWorks),
      regions: getRegionOptions(allWorks),
      genres: getGenreOptions(allWorks),
    }),
    []
  );

  const filtered = useMemo(() => sortWorks(filterWorks(allWorks, filters), sortKey), [filters, sortKey]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const visibleWorks = paginate(filtered, currentPage);

  const meta = validType
    ? {
        title: `痴汉电车 - ${TYPE_LABELS[validType]}分类列表`,
        description: `痴汉电车${TYPE_LABELS[validType]}分类列表，收录相关${TYPE_LABELS[validType]}作品，支持按年份、地区与剧情类型筛选，并提供完整剧情简介。`,
      }
    : {
        title: "痴汉电车 - 全部影视作品与剧情专题",
        description:
          "浏览痴汉电车题材全部影视作品，按电影、电视剧、上映年份、地区与剧情类型组合筛选，快速找到想看的作品及其剧情介绍。",
      };

  usePageMeta({
    ...meta,
    keywords: `痴汉电车,痴汉电车${validType ? TYPE_LABELS[validType] : "影视作品"},剧情介绍,影视推荐`,
    suffix: false,
  });

  const toggleFilter = (group: keyof WorkFilters, value: string) => {
    setFilters((prev) => {
      const current = prev[group];
      const next = current.includes(value) ? current.filter((item) => item !== value) : [...current, value];
      return { ...prev, [group]: next };
    });
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("page");
      return next;
    }, { replace: true });
  };

  const resetFilters = () => {
    setFilters({ ...EMPTY_FILTERS, types: validType ? [validType] : [] });
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("page");
      return next;
    }, { replace: true });
  };

  const changeSort = (value: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("sort", value);
      next.delete("page");
      return next;
    }, { replace: true });
  };

  const changePage = (nextPage: number) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (nextPage <= 1) {
        next.delete("page");
      } else {
        next.set("page", String(nextPage));
      }
      return next;
    }, { replace: true });
    listRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const categoryLabel = validType ? TYPE_LABELS[validType] : "全部作品";

  return (
    <SiteLayout>
      <section className="relative w-full pt-28 md:pt-32 pb-8 md:pb-10 px-4 md:px-8 border-b border-background-200/70 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-primary-900/25 via-background-50 to-background-50" />
        <div className="relative max-w-[1400px] mx-auto">
          <nav className="flex items-center gap-2 text-xs text-foreground-500" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-300 transition-colors">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-800">{categoryLabel}</span>
          </nav>

          <div className="mt-6 flex flex-col md:flex-row md:items-end md:justify-between gap-5">
            <div className="max-w-2xl">
              <div className="flex items-center gap-3">
                <span className="w-8 h-[2px] bg-primary-500" />
                <span className="font-label text-[11px] tracking-[0.28em] text-primary-300 uppercase">
                  {validType === "movie" ? "FEATURE FILMS" : validType === "tv" ? "SERIES ARCHIVE" : "FULL ARCHIVE"}
                </span>
              </div>
              <h1 className="mt-3 font-heading text-3xl md:text-4xl font-semibold text-foreground-950">
                {validType ? `${categoryLabel}分类` : "全部作品 · 剧情专题"}
              </h1>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                {validType === "movie"
                  ? "收录该题材的长片作品，按上映年份、地区与剧情类型整理，每部均附完整剧情与看点分析。"
                  : validType === "tv"
                  ? "收录连续剧与迷你剧，含分集体量与剧情走向说明，适合想慢慢追完一个故事的观众。"
                  : "电影与电视剧统一归档，可按类型、年份、地区与剧情标签自由组合筛选。"}
              </p>
            </div>

            <div className="flex items-center gap-3">
              <div className="hidden sm:flex items-center gap-2 px-4 py-2 rounded-md bg-background-100 border border-background-200/70">
                <i className="ri-stack-line text-foreground-500 text-sm" />
                <span className="text-xs text-foreground-600">
                  共 <span className="text-foreground-950 font-medium">{filtered.length}</span> 部作品
                </span>
              </div>
              <div className="relative">
                <select
                  value={sortKey}
                  onChange={(event) => changeSort(event.target.value)}
                  className="appearance-none bg-background-100 border border-background-200/70 rounded-md pl-4 pr-9 py-2.5 text-sm text-foreground-800 outline-none cursor-pointer focus:border-primary-500"
                  aria-label="排序方式"
                >
                  {SORT_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value} className="bg-background-100">
                      {option.label}
                    </option>
                  ))}
                </select>
                <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-foreground-500" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-8 md:py-12">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-[260px_minmax(0,1fr)] gap-8">
          <aside className="hidden lg:block">
            <div className="sticky top-24 rounded-lg bg-background-100/70 border border-background-200/70 p-5">
              <FilterPanel filters={filters} options={options} onToggle={toggleFilter} onReset={resetFilters} />
            </div>
          </aside>

          <div ref={listRef} className="min-w-0 scroll-mt-24">
            <div className="lg:hidden flex items-center justify-between gap-3 mb-5">
              <p className="text-sm text-foreground-600">
                共 <span className="text-foreground-950 font-medium">{filtered.length}</span> 部作品
              </p>
              <button
                type="button"
                onClick={() => setFilterOpen(true)}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-background-200/80 text-sm text-foreground-800 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-filter-3-line" />
                筛选
              </button>
            </div>

            {visibleWorks.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 md:gap-6">
                {visibleWorks.map((work) => (
                  <MovieCard key={work.id} work={work} />
                ))}
              </div>
            ) : (
              <div className="rounded-lg border border-background-200/70 bg-background-100/60 py-20 px-6 text-center">
                <i className="ri-inbox-archive-line text-4xl text-background-500" />
                <p className="mt-4 font-heading text-lg text-foreground-900">当前筛选条件下暂无作品</p>
                <p className="mt-2 text-sm text-foreground-600">试试减少几个筛选条件，或直接浏览全部作品。</p>
                <button
                  type="button"
                  onClick={resetFilters}
                  className="mt-6 px-5 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-foreground-950 text-sm transition-colors cursor-pointer whitespace-nowrap"
                >
                  清除筛选条件
                </button>
              </div>
            )}

            {filtered.length > 0 && (
              <div className="mt-9 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <p className="text-xs text-foreground-500">
                  第 {currentPage} / {totalPages} 页 · 每页 {PAGE_SIZE} 部
                </p>
                <Pagination page={currentPage} totalPages={totalPages} onChange={changePage} />
              </div>
            )}
          </div>
        </div>
      </section>

      {filterOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <button
            type="button"
            className="absolute inset-0 bg-background-50/85 backdrop-blur-sm cursor-pointer"
            aria-label="关闭筛选"
            onClick={() => setFilterOpen(false)}
          />
          <div className="absolute left-0 top-0 bottom-0 w-[86%] max-w-sm bg-background-50 border-r border-background-200/70 p-5 overflow-y-auto animate-slide-down">
            <div className="flex items-center justify-between mb-5">
              <span className="font-heading text-lg text-foreground-950">筛选作品</span>
              <button
                type="button"
                onClick={() => setFilterOpen(false)}
                className="w-9 h-9 rounded-md border border-background-200/80 flex items-center justify-center text-foreground-700 cursor-pointer"
                aria-label="关闭"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <FilterPanel filters={filters} options={options} onToggle={toggleFilter} onReset={resetFilters} />
            <button
              type="button"
              onClick={() => setFilterOpen(false)}
              className="mt-7 w-full py-3 rounded-md bg-primary-500 hover:bg-primary-600 text-foreground-950 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
            >
              查看 {filtered.length} 部作品
            </button>
          </div>
        </div>
      )}
    </SiteLayout>
  );
}